import { useEffect, useState } from 'react';
import { useConst } from '@fluentui/react-hooks';
import { useWebView } from 'store/webView/useWebView';

import TelemetryProvider, { Properties, UserActivityGroup } from './TelemetryProvider';

/**
 * Helper hook to get information about screen resolution
 */
export const useScreenSizeStats = () => {
  const { webViewPlatform } = useWebView();
  return {
    viewportHeight: window.innerHeight ?? undefined,
    viewportWidth: window.innerWidth ?? undefined,
    webViewPlatform: webViewPlatform ?? undefined,
  };
};

/**
 * Function wrapper to log click activity
 *
 * @param eventName the event name(s) to log
 * @param onClick actual onClick handler
 * @param properties additional props for telemetry
 * @param eventGroup override the default event group (default: `ClickActivity`)
 */
export const logClick = <F extends (...args: never[]) => unknown>(
  eventName: string | string[],
  onClick: F,
  properties?: Properties,
  eventGroup: UserActivityGroup = 'ClickActivity'
): F => <F>((...args: never[]) => {
    if (typeof eventName === 'string') {
      TelemetryProvider.Instance.logUserActivity(eventName, eventGroup, properties);
    } else {
      eventName.forEach((en) => TelemetryProvider.Instance.logUserActivity(en, eventGroup, properties));
    }
    return onClick(...args);
  });

/**
 * Log alert-related click
 *
 * @param eventName the event name to log
 * @param onClick actual onClick handler
 * @param properties additional props for telemetry
 */
export const logAlertClick = <F extends (...args: never[]) => unknown>(eventName: string, onClick: F, properties?: Properties): F =>
  <F>((...args: never[]) => {
    TelemetryProvider.Instance.logUserActivity(eventName, 'AlertActivity', properties);
    return onClick(...args);
  });

/**
 * Helper function for logging view activity
 *
 * @param viewName the name of page/dialog/component to be logged
 * @param properties additional props for telemetry
 * @param eventGroup override the default event group (default: `PageViewActivity`)
 */
export const logView = (viewName: string | string[], properties?: Properties, eventGroup: UserActivityGroup = 'PageViewActivity') => {
  if (typeof viewName === 'string') {
    TelemetryProvider.Instance.logUserActivity(viewName, eventGroup, properties);
  } else {
    viewName.forEach((vn) => TelemetryProvider.Instance.logUserActivity(vn, eventGroup, properties));
  }
};

/**
 * Helper hook to log a user view activity on component mount
 *
 * @param viewName the name of page/dialog/component to be logged
 * @param properties additional props for telemetry (note that this will only be used on mount, any subsequent changes to the props will be ignored)
 * @param eventGroup override the default event group (default: `PageViewActivity`)
 */
export const useLogViewOnMount = (viewName: string | string[], properties?: Properties, eventGroup?: UserActivityGroup) => {
  const logProps = useConst<Properties | undefined>(properties);
  const screenSizeStats = useConst(useScreenSizeStats());

  useEffect(() => {
    if (viewName) {
      logView(viewName, { ...screenSizeStats, ...logProps }, eventGroup);
    }
  }, [eventGroup, screenSizeStats, logProps, viewName]);
};

/**
 * Helper hook to log a user view activity when {@link condition} is true
 * Note that this will only log once per component mount
 *
 * @param viewName the name of page/dialog/component to be logged
 * @param condition the condition to check before logging
 * @param properties additional props for telemetry (note that this will only be used on mount, any subsequent changes to the props will be ignored)
 * @param eventGroup override the default event group (default: `PageViewActivity`)
 */
export const useLogViewOnCondition = (
  viewName: string,
  condition: boolean,
  properties?: Properties,
  eventGroup?: UserActivityGroup
) => {
  const [isLogged, setIsLogged] = useState(false);
  const screenSizeStats = useScreenSizeStats();

  useEffect(() => {
    if (condition && !isLogged) {
      logView(viewName, { ...screenSizeStats, ...properties }, eventGroup);
      setIsLogged(true);
    }
  }, [condition, eventGroup, screenSizeStats, isLogged, properties, viewName]);
};
